import React, { Component } from 'react';
import { Grid, CircularProgress, Tabs, Tab, Box } from '@mui/material';
import { withStyles } from '@mui/styles';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import FacilityContacts from '../FacilityContacts/FacilityContacts';
import ActiveAssignmentList from '../ActiveAssignmentList/ActiveAssignmentList';
import LocumTenensAfterHourSupportContact from '../LocumTenensContacts/LocumTenensAfterHourSupportContact';
import ActiveAssignmentDetailView from '../ActiveAssignmentDetailView/ActiveAssignmentDetailView';
import { getPresents } from '../../../services/PresentsService';
import UserContext from '../../../contexts/userContext';
import PresentsWidgetComponent from '../PresentsWidgetComponent/PresentsWidgetComponent';
import { getAssignments, getAssignmentTeam } from '../../../services/AssignmentsService';
import AssignmentDocumentComponentV2 from '../AssignmentDocumentComponent/AssignmentDocumentComponentV2';
import { FeatureFlag } from '../../GlobalComponents/FeatureFlagComponent/FeatureFlag';
import TravelV2 from '../Travel/TravelV2';
import TravelV3 from '../Travel/TravelV3';
import YourTeamGrid from '../YourTeam/YourTeamGrid';
import DayOneDetails from '../DayOneDetails/DayOneDetails';

const styles = () => ({
    contactsWidget: {
        marginTop: '20px',
    },
});

class ActiveAssignmentsYourTeam extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listView: true,
            selectedAssignmentId: '',
            assignments: [],
            assignmentTeam: [],
            isTeamLoading: false,
            activeTab: 0,
            url: {
                location: 'active',
                id: null,
            },
            isLoading: true,
        };
    }

    async componentDidMount() {
        await this.getUrlParams();
        const { externalId } = this.context;
        const { setAssignmentPageError } = this.props;

        await Promise.all([getPresents(externalId), getAssignments(externalId, 'active', true)])
            .then(([presents, assignments]) => {
                this.setState(
                    {
                        presents,
                        assignments,
                        isLoading: false,
                    },
                    () => {
                        // Load team data if there's an assignment ID in the URL
                        const { match } = this.props;
                        if (match.params.id) {
                            this.loadAssignmentTeam();
                        }
                    }
                );
            })
            .catch((e) => {
                setAssignmentPageError(e);
            });
    }

    async componentDidUpdate(prevProps, prevState) {
        const { match } = this.props;
        if (prevState.url.id !== match.params.id) {
            await this.getUrlParams();
            // Load team data when assignment changes
            if (match.params.id) {
                // Use setTimeout to ensure state is updated before loading team
                setTimeout(() => {
                    this.loadAssignmentTeam();
                }, 0);
            }
        }
    }

    getUrlParams() {
        const { match } = this.props;
        this.setState({ url: { ...match.params } });
    }

    loadAssignmentTeam = async () => {
        const { externalId } = this.context;
        const currentAssignment = this.getAssignmentBasedOnId();

        if (!currentAssignment) return;

        this.setState({ isTeamLoading: true });
        try {
            const assignmentTeam = await getAssignmentTeam(
                externalId,
                currentAssignment.id,
                'active'
            );
            this.setState({
                assignmentTeam,
                isTeamLoading: false,
            });
        } catch (error) {
            console.error('Error loading assignment team:', error);
            this.setState({ isTeamLoading: false });
        }
    };

    updateActiveAssignments = async () => {
        await this.setState({ isLoading: true });
        const { externalId } = this.context;
        const { setAssignmentPageError } = this.props;
        const assignments = await getAssignments(externalId, 'active', true).catch((e) => {
            setAssignmentPageError(e);
        });
        this.setState({
            assignments,
            isLoading: false,
        });
    };

    toggleAssignmentView = async (assignmentId) => {
        const state = { ...this.state };
        const { history } = this.props;
        state.url.id = assignmentId;
        history.push(`/assignments/active/${assignmentId}`);
        this.setState({ url: state.url }, () => {
            // Load team data after state is set
            this.loadAssignmentTeam();
        });
    };

    getAssignmentBasedOnId = () => {
        const { url, assignments } = this.state;
        const assignment = assignments.filter((item) => item.id.toString() === url.id.toString());
        return assignment[0];
    };

    renderAssignmentView = () => {
        const { url, assignments, activeTab, assignmentTeam, isTeamLoading } = this.state;
        const { externalId } = this.context;

        if (!url.id) {
            return (
                <ActiveAssignmentList
                    assignments={assignments}
                    toggleAssignmentView={this.toggleAssignmentView}
                />
            );
        }

        const currentAssignment = this.getAssignmentBasedOnId();
        if (!currentAssignment) return <p>Error, Assignment not found</p>;

        const hasDayOneDetails = currentAssignment.areDayOneDetailsPresent;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Box>
                        <ActiveAssignmentDetailView
                            assignment={currentAssignment}
                            externalId={externalId}
                            contacts={currentAssignment.locumtenensContact}
                            assignmentTeam={assignmentTeam}
                        />

                        <Box sx={{ width: '100%', mt: 3 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, newValue) => this.setState({ activeTab: newValue })}
                            >
                                {hasDayOneDetails && <Tab label="Day One" />}
                                <Tab label="Meet Your Team" />
                                <Tab label="Travel" />
                                <Tab label="Documents" />
                            </Tabs>

                            <Box>
                                {hasDayOneDetails && activeTab === 0 && (
                                    <DayOneDetails
                                        assignment={currentAssignment}
                                        assignmentTeam={assignmentTeam}
                                    />
                                )}

                                {activeTab === (hasDayOneDetails ? 1 : 0) &&
                                    (isTeamLoading ? (
                                        <CircularProgress color="primary" />
                                    ) : (
                                        <YourTeamGrid yourteam={assignmentTeam} />
                                    ))}

                                {activeTab === (hasDayOneDetails ? 2 : 1) && (
                                    <>
                                        <FeatureFlag
                                            name="TravelV3"
                                            isFlagEnabled="false"
                                            fallbackValue={false}
                                        >
                                            <TravelV2
                                                assignment={currentAssignment}
                                                externalId={externalId}
                                                filter="active"
                                            />
                                        </FeatureFlag>

                                        <FeatureFlag
                                            name="TravelV3"
                                            isFlagEnabled="true"
                                            fallbackValue={false}
                                        >
                                            <TravelV3
                                                assignment={currentAssignment}
                                                externalId={externalId}
                                                filter="active"
                                            />
                                        </FeatureFlag>
                                    </>
                                )}

                                {activeTab === (hasDayOneDetails ? 3 : 2) && (
                                    <AssignmentDocumentComponentV2
                                        assignmentName={currentAssignment.name}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ mt: -3 }}>{this.renderWidgetsBasedOnView()}</Box>
                </Grid>
            </Grid>
        );
    };

    renderDetailViewWidgets = () => {
        const { classes } = this.props;
        const currentAssignment = this.getAssignmentBasedOnId();
        if (!currentAssignment) return null;
        return (
            <div className={classes.contactsWidget}>
                {this.renderLTAfterHourSupportContact()}
                {this.renderFacilityContact(
                    currentAssignment.facilityId,
                    currentAssignment.facilityContacts,
                    currentAssignment.displayName
                )}
            </div>
        );
    };

    renderFacilityContact = (facilityId, contacts, primaryWorkLocation) => {
        if (!facilityId) return null;
        if (contacts && Array.isArray(contacts) && contacts.length > 0) {
            return (
                <FacilityContacts
                    facilityId={facilityId}
                    contacts={contacts}
                    primaryWorkLocation={primaryWorkLocation}
                    updateAssignments={this.updateActiveAssignments}
                />
            );
        }
        return (
            <FacilityContacts
                facilityId={facilityId}
                contacts={[]}
                updateAssignments={this.updateActiveAssignments}
            />
        );
    };

    renderLTAfterHourSupportContact = () => <LocumTenensAfterHourSupportContact />;

    renderWidgetsBasedOnView = () => {
        const { url } = this.state;
        const { presents } = this.state;
        if (url.id) {
            return this.renderDetailViewWidgets();
        }
        return <PresentsWidgetComponent isFirst presents={presents} />;
    };

    render() {
        const { isLoading } = this.state;
        if (isLoading) {
            return (
                <div>
                    <CircularProgress color="primary" />
                </div>
            );
        }
        return <>{this.renderAssignmentView()}</>;
    }
}
ActiveAssignmentsYourTeam.contextType = UserContext;

ActiveAssignmentsYourTeam.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string,
            status: PropTypes.string.isRequired,
        }),
    }).isRequired,
};

export default withRouter(withStyles(styles)(ActiveAssignmentsYourTeam));
